export const CONTENT_TOC_LINK_CLASSES =
	'has-fs-16-font-size leading-none text-inherit underline decoration-[1px] underline-offset-[3px]';
export const CONTENT_TOC_LIST_ITEM_CLASSES = 'mb-[15px] leading-none';
export const MOBILE_TOC_LINK_CLASSES =
	'has-fs-16-font-size block border-b-0 py-[14px] font-[600] leading-[1.2] text-inherit no-underline whitespace-normal !mb-0';
export const MOBILE_TOC_LIST_ITEM_CLASSES =
	'border-t border-solid border-[rgb(0_20_87_/_10%)] first:border-t-0';

function slugify(value) {
	const normalizedValue =
		typeof value.normalize === 'function' ? value.normalize('NFKD') : value;

	return normalizedValue
		.toLowerCase()
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

function buildUniqueId(element, label, seenIds) {
	const currentId = (element.id || '').trim();

	if (
		currentId &&
		!seenIds.has(currentId) &&
		document.getElementById(currentId) === element
	) {
		seenIds.add(currentId);
		return currentId;
	}

	const baseId = slugify(currentId || label) || 'section';
	let candidateId = baseId;
	let suffix = 2;

	while (
		seenIds.has(candidateId) ||
		(document.getElementById(candidateId) &&
			document.getElementById(candidateId) !== element)
	) {
		candidateId = `${baseId}-${suffix}`;
		suffix += 1;
	}

	element.id = candidateId;
	seenIds.add(candidateId);

	return candidateId;
}

function setElementVisibility(element, isVisible, displayValue = '') {
	if (!element) {
		return;
	}

	element.hidden = !isVisible;
	element.style.display = isVisible ? displayValue : 'none';
}

function createWindowedRange(length, activeIndex, visibleCount) {
	if (length <= visibleCount) {
		return {
			start: 0,
			end: length,
		};
	}

	const maxStart = length - visibleCount;
	const start = Math.max(0, Math.min(activeIndex - 1, maxStart));

	return {
		start,
		end: start + visibleCount,
	};
}

function createTocLink(item, linkClassName) {
	const link = document.createElement('a');

	link.href = `#${item.id}`;
	link.dataset.targetId = item.id;
	link.textContent = item.label;
	link.className = linkClassName;

	return link;
}

function buildMenuItems(content, ignoredAncestorClasses = []) {
	const seenIds = new Set();

	return Array.from(content.querySelectorAll('h2'))
		.filter(
			(element) =>
				!ignoredAncestorClasses.some((className) =>
					element.closest(`.${className}`)
				)
		)
		.map((element) => {
			const label = element.textContent.replace(/\s+/g, ' ').trim();

			if (!label) {
				return null;
			}

			return {
				element,
				id: buildUniqueId(element, label, seenIds),
				label,
			};
		})
		.filter(Boolean);
}

function renderFullToc(tocList, items, linkClassName, listItemClassName = '') {
	tocList.innerHTML = '';

	items.forEach((item) => {
		const listItem = document.createElement('li');
		listItem.className = listItemClassName;
		listItem.appendChild(createTocLink(item, linkClassName));
		tocList.appendChild(listItem);
	});
}

function populateCollapsedTocWindow(
	tocWindow,
	items,
	linkClassName,
	listItemClassName = ''
) {
	tocWindow.innerHTML = '';

	items.forEach((item, index) => {
		const listItem = document.createElement('li');
		listItem.className = listItemClassName;
		listItem.style.setProperty('--fc-toc-window-index', index);
		listItem.appendChild(createTocLink(item, linkClassName));
		tocWindow.appendChild(listItem);
	});
}

function measureCollapsedWindow(tocWindow, visibleCount, startIndex) {
	const listItems = Array.from(tocWindow.children);

	if (!listItems.length) {
		return null;
	}

	const clampedStartIndex = Math.max(
		0,
		Math.min(startIndex, Math.max(0, listItems.length - visibleCount))
	);
	const endIndex = Math.min(
		listItems.length - 1,
		clampedStartIndex + visibleCount - 1
	);
	const startItem = listItems[clampedStartIndex];
	const endItem = listItems[endIndex];

	if (!startItem || !endItem) {
		return null;
	}

	return {
		height: endItem.offsetTop + endItem.offsetHeight - startItem.offsetTop,
		offset: startItem.offsetTop,
	};
}

function measureMaxCollapsedWindowHeight(tocWindow, itemCount, visibleCount) {
	return Math.max(
		...Array.from(
			{
				length: Math.max(1, itemCount - visibleCount + 1),
			},
			(_, index) =>
				measureCollapsedWindow(tocWindow, visibleCount, index)
					?.height || 0
		)
	);
}

function getCollapsedVisibleCount(tocWindow, items, options) {
	const defaultCount = Math.min(options.defaultVisibleItems, items.length);

	if (!options.responsiveVisibleItems || items.length <= defaultCount) {
		return defaultCount;
	}

	const screenHeight = window.visualViewport?.height || window.innerHeight;
	const availableHeight = Math.max(
		options.minimumViewportHeight || 0,
		screenHeight * (options.viewportHeightRatio || 1)
	);
	const maximumCount = Math.max(defaultCount, items.length - 1);
	let visibleCount = defaultCount;
	let measuredHeight = measureMaxCollapsedWindowHeight(
		tocWindow,
		items.length,
		visibleCount
	);

	for (let count = defaultCount + 1; count <= maximumCount; count += 1) {
		const candidateHeight = measureMaxCollapsedWindowHeight(
			tocWindow,
			items.length,
			count
		);

		if (
			candidateHeight > availableHeight &&
			measuredHeight >= (options.minimumViewportHeight || 0)
		) {
			break;
		}

		visibleCount = count;
		measuredHeight = candidateHeight;
	}

	return visibleCount;
}

function renderCollapsedTocWindow(block, items, activeId, options) {
	const viewport = block.querySelector(options.tocViewportSelector);
	const tocWindow = block.querySelector(options.tocWindowSelector);

	if (!viewport || !tocWindow) {
		return;
	}

	if (!tocWindow.children.length) {
		populateCollapsedTocWindow(
			tocWindow,
			items,
			options.linkClassName,
			options.listItemClassName
		);
	}

	const visibleCount = getCollapsedVisibleCount(tocWindow, items, options);

	tocWindow.querySelectorAll('a[data-target-id]').forEach((link) => {
		link.classList.toggle(
			options.activeClass,
			link.getAttribute('data-target-id') === activeId
		);
	});

	const activeIndex = Math.max(
		0,
		items.findIndex((item) => item.id === activeId)
	);
	const { start } = createWindowedRange(
		items.length,
		activeIndex,
		visibleCount
	);
	const currentWindow = measureCollapsedWindow(
		tocWindow,
		visibleCount,
		start
	);
	const viewportHeight = measureMaxCollapsedWindowHeight(
		tocWindow,
		items.length,
		visibleCount
	);

	if (!currentWindow || !viewportHeight) {
		return;
	}

	viewport.style.height = `${viewportHeight}px`;
	tocWindow.style.transform = `translateY(${-currentWindow.offset}px)`;

	const listItems = Array.from(tocWindow.children);
	viewport.dataset.hasOverflowTop = start > 0 ? 'true' : 'false';
	viewport.dataset.hasOverflowBottom =
		start + visibleCount < listItems.length ? 'true' : 'false';

	listItems.forEach((item, index) => {
		const isInVisibleWindow =
			index >= start && index < start + visibleCount;
		item.classList.toggle('is-in-window', isInVisibleWindow);

		if (!isInVisibleWindow) {
			item.classList.remove('is-window-edge-top', 'is-window-edge-bottom');
			return;
		}

		item.classList.toggle('is-window-edge-top', index === start);
		item.classList.toggle(
			'is-window-edge-bottom',
			index === Math.min(listItems.length - 1, start + visibleCount - 1)
		);
	});
}

function bindCollapsedWindowResize(block, items, getActiveId, options) {
	let animationFrame = null;
	const rerender = () => {
		if (animationFrame !== null) {
			return;
		}

		animationFrame = window.requestAnimationFrame(() => {
			animationFrame = null;
			renderCollapsedTocWindow(block, items, getActiveId(), options);
		});
	};

	window.addEventListener('resize', rerender, { passive: true });

	if (typeof ResizeObserver === 'undefined') {
		return;
	}

	const viewport = block.querySelector(options.tocViewportSelector);
	const tocWindow = block.querySelector(options.tocWindowSelector);

	if (!viewport || !tocWindow) {
		return;
	}

	const resizeObserver = new ResizeObserver(rerender);
	resizeObserver.observe(tocWindow);

	block.querySelectorAll(`${options.contentSelector} img`).forEach((image) => {
		resizeObserver.observe(image);
	});
}

function syncCollapsedState(block, items, activeId, options) {
	const tocViewport = block.querySelector(options.tocViewportSelector);
	const tocFull = block.querySelector(options.tocListSelector);
	const toggle = block.querySelector(options.toggleSelector);
	const toggleWrap = block.querySelector(options.toggleWrapSelector);

	if (!tocViewport || !tocFull || !toggle || !toggleWrap) {
		return;
	}

	if (items.length <= options.defaultVisibleItems) {
		setElementVisibility(toggle, false);
		setElementVisibility(toggleWrap, false);
		setElementVisibility(tocViewport, false);
		setElementVisibility(tocFull, true, options.tocListDisplay);
		return;
	}

	const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

	setElementVisibility(toggleWrap, true);
	setElementVisibility(toggle, true, options.toggleDisplay);
	setElementVisibility(tocViewport, !isExpanded, options.viewportDisplay);
	setElementVisibility(tocFull, isExpanded, options.tocListDisplay);
	toggle.textContent = isExpanded
		? options.showLessLabel
		: options.showMoreLabel;

	if (!isExpanded) {
		renderCollapsedTocWindow(block, items, activeId, options);
	}
}

function bindTocToggle(block, items, getActiveId, options) {
	const toggle = block.querySelector(options.toggleSelector);

	if (!toggle) {
		return;
	}

	toggle.addEventListener('click', () => {
		const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
		toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
		syncCollapsedState(block, items, getActiveId(), options);
	});
}

function keepMirroredActiveLinkInView(block, activeId, options) {
	if (
		!options.mirroredTocWrapperSelector ||
		!options.mirroredTocScrollSelector
	) {
		return;
	}

	block
		.querySelectorAll(options.mirroredTocWrapperSelector)
		.forEach((wrapper) => {
			const scrollContainer = wrapper.querySelector(
				options.mirroredTocScrollSelector
			);
			const activeLink = wrapper.querySelector(
				`a[data-target-id="${CSS.escape(activeId)}"]`
			);

			if (!scrollContainer || !activeLink) {
				return;
			}

			const containerRect = scrollContainer.getBoundingClientRect();
			const linkRect = activeLink.getBoundingClientRect();
			const isOutsideVisibleArea =
				linkRect.left < containerRect.left ||
				linkRect.right > containerRect.right;

			if (!isOutsideVisibleArea) {
				return;
			}

			const prefersReducedMotion = window.matchMedia(
				'(prefers-reduced-motion: reduce)'
			).matches;
			const left =
				scrollContainer.scrollLeft +
				linkRect.left -
				containerRect.left -
				(scrollContainer.clientWidth - linkRect.width) / 2;

			scrollContainer.scrollTo({
				left: Math.max(0, left),
				behavior: prefersReducedMotion ? 'auto' : 'smooth',
			});
		});
}

function bindMirroredTocControls(block, options) {
	if (
		!options.mirroredTocWrapperSelector ||
		!options.mirroredTocScrollSelector ||
		!options.mirroredTocPreviousSelector ||
		!options.mirroredTocNextSelector
	) {
		return;
	}

	block
		.querySelectorAll(options.mirroredTocWrapperSelector)
		.forEach((wrapper) => {
			const scrollContainer = wrapper.querySelector(
				options.mirroredTocScrollSelector
			);
			const previousButton = wrapper.querySelector(
				options.mirroredTocPreviousSelector
			);
			const nextButton = wrapper.querySelector(
				options.mirroredTocNextSelector
			);

			if (!scrollContainer || !previousButton || !nextButton) {
				return;
			}

			const updateControls = () => {
				const maxScrollLeft = Math.max(
					0,
					scrollContainer.scrollWidth - scrollContainer.clientWidth
				);
				const hasOverflow = maxScrollLeft > 1;

				previousButton.hidden = !hasOverflow;
				nextButton.hidden = !hasOverflow;
				previousButton.disabled = scrollContainer.scrollLeft <= 1;
				nextButton.disabled =
					scrollContainer.scrollLeft >= maxScrollLeft - 1;
			};

			const scrollMenu = (direction) => {
				const prefersReducedMotion = window.matchMedia(
					'(prefers-reduced-motion: reduce)'
				).matches;
				const distance = Math.max(
					180,
					scrollContainer.clientWidth * 0.7
				);

				scrollContainer.scrollBy({
					left: distance * direction,
					behavior: prefersReducedMotion ? 'auto' : 'smooth',
				});
			};

			previousButton.addEventListener('click', () => scrollMenu(-1));
			nextButton.addEventListener('click', () => scrollMenu(1));
			scrollContainer.addEventListener('scroll', updateControls, {
				passive: true,
			});

			let isPointerDown = false;
			let isPointerDragging = false;
			let pointerStartX = 0;
			let pointerStartScrollLeft = 0;
			let suppressClick = false;

			scrollContainer.addEventListener('pointerdown', (event) => {
				if (event.pointerType !== 'mouse' || event.button !== 0) {
					return;
				}

				isPointerDown = true;
				pointerStartX = event.clientX;
				pointerStartScrollLeft = scrollContainer.scrollLeft;
			});

			scrollContainer.addEventListener('pointermove', (event) => {
				if (!isPointerDown) {
					return;
				}

				const distance = event.clientX - pointerStartX;

				if (Math.abs(distance) <= 4) {
					return;
				}

				if (!isPointerDragging) {
					isPointerDragging = true;
					suppressClick = true;
					scrollContainer.classList.add('is-dragging');
					scrollContainer.setPointerCapture(event.pointerId);
				}

				scrollContainer.scrollLeft =
					pointerStartScrollLeft - distance;
				event.preventDefault();
			});

			const stopPointerDrag = (event) => {
				if (!isPointerDown) {
					return;
				}

				isPointerDown = false;

				if (isPointerDragging) {
					isPointerDragging = false;
					scrollContainer.classList.remove('is-dragging');

					if (scrollContainer.hasPointerCapture(event.pointerId)) {
						scrollContainer.releasePointerCapture(event.pointerId);
					}
				}

				window.setTimeout(() => {
					suppressClick = false;
				}, 0);
			};

			scrollContainer.addEventListener('pointerup', stopPointerDrag);
			scrollContainer.addEventListener('pointercancel', stopPointerDrag);
			scrollContainer.addEventListener(
				'click',
				(event) => {
					if (!suppressClick) {
						return;
					}

					event.preventDefault();
					event.stopPropagation();
				},
				true
			);

			if (typeof ResizeObserver !== 'undefined') {
				const resizeObserver = new ResizeObserver(updateControls);
				resizeObserver.observe(scrollContainer);
				resizeObserver.observe(
					scrollContainer.querySelector(
						options.mirroredTocListSelector
					) || scrollContainer
				);
			} else {
				window.addEventListener('resize', updateControls);
			}

			updateControls();
		});
}

function bindActiveSectionState(block, items, options) {
	if (!items.length || typeof IntersectionObserver === 'undefined') {
		return {
			getActiveId: () => items[0]?.id || '',
		};
	}

	const observedItemsById = new Map(items.map((item) => [item.id, item]));
	let currentActiveId = items[0].id;

	const setActive = (id) => {
		currentActiveId = id;

		block.querySelectorAll('a[data-target-id]').forEach((link) => {
			link.classList.toggle(
				options.activeClass,
				link.getAttribute('data-target-id') === id
			);
		});

		keepMirroredActiveLinkInView(block, id, options);
		syncCollapsedState(block, items, currentActiveId, options);
	};

	const visibleEntries = new Map();
	const syncActiveState = () => {
		const candidate = items
			.filter((item) => visibleEntries.get(item.id)?.isIntersecting)
			.sort((first, second) => {
				const firstTop = Math.abs(
					visibleEntries.get(first.id).boundingClientRect.top
				);
				const secondTop = Math.abs(
					visibleEntries.get(second.id).boundingClientRect.top
				);

				if (firstTop !== secondTop) {
					return firstTop - secondTop;
				}

				return items.indexOf(first) - items.indexOf(second);
			})[0];

		if (candidate) {
			setActive(candidate.id);
		}
	};

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				visibleEntries.set(entry.target.id, entry);
			});

			syncActiveState();
		},
		{
			// Headings settle at least 96px from the top after anchor navigation.
			// Keep the activation line above that position so a clicked link
			// remains active when smooth scrolling finishes.
			rootMargin: '-80px 0px -60% 0px',
			threshold: [0.15, 0.35, 0.6],
		}
	);

	block.querySelectorAll('a[data-target-id]').forEach((link) => {
		link.addEventListener('click', () => {
			const targetId = link.getAttribute('data-target-id');
			setActive(observedItemsById.get(targetId)?.id || currentActiveId);
		});
	});

	items.forEach((item) => observer.observe(item.element));
	setActive(currentActiveId);

	return {
		getActiveId: () => currentActiveId,
	};
}

export function initH2ContentToc(block, options) {
	const content = block.querySelector(options.contentSelector);
	const toc = block.querySelector(options.tocListSelector);
	const tocWrapper = block.querySelector(options.tocWrapperSelector);

	if (!content || !toc || !tocWrapper) {
		return;
	}

	const items = buildMenuItems(content, options.ignoredAncestorClasses);
	renderFullToc(toc, items, options.linkClassName, options.listItemClassName);
	tocWrapper.hidden = items.length === 0;

	if (options.mirroredTocListSelector) {
		block
			.querySelectorAll(options.mirroredTocListSelector)
			.forEach((mirroredToc) =>
				renderFullToc(
					mirroredToc,
					items,
					options.mirroredLinkClassName || options.linkClassName,
					options.mirroredListItemClassName
				)
			);
	}

	if (options.mirroredTocWrapperSelector) {
		block
			.querySelectorAll(options.mirroredTocWrapperSelector)
			.forEach((mirroredTocWrapper) => {
				mirroredTocWrapper.hidden = items.length === 0;
			});
	}

	if (typeof options.onItemsChange === 'function') {
		options.onItemsChange(items);
	}

	const activeState = bindActiveSectionState(block, items, options);
	bindMirroredTocControls(block, options);
	syncCollapsedState(block, items, activeState.getActiveId(), options);
	bindTocToggle(block, items, activeState.getActiveId, options);
	bindCollapsedWindowResize(block, items, activeState.getActiveId, options);
}
